import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { HardDrive, CheckCircle, AlertCircle, ExternalLink, Folder, RefreshCw, Database } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

export default function StorageSettings() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [status, setStatus] = useState(null);
  const [savingMode, setSavingMode] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      // Run health check first to refresh stale tokens
      await axios.get(`${API_URL}/api/storage/health`, { withCredentials: true }).catch(() => {});
      const response = await axios.get(
        `${API_URL}/api/storage/status`,
        { withCredentials: true }
      );
      setStatus(response.data);
    } catch (err) {
      console.error('Error loading storage status:', err);
      toast.error('Failed to load storage status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for OAuth callback success/error messages FIRST
    const params = new URLSearchParams(window.location.search);
    const hasSuccess = params.get('success') === 'connected';
    const hasError = params.get('error');
    
    if (hasSuccess) {
      toast.success('Dropbox connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (hasError) {
      const errorMessages = {
        'oauth_failed': 'Dropbox authorization failed',
        'no_code': 'No authorization code received',
        'connection_failed': 'Failed to establish connection',
        'invalid_state': 'Invalid session state - please try again'
      };
      toast.error(errorMessages[hasError] || 'Connection error');
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Always load status (especially important after callback)
    loadStatus();
  }, [loadStatus]);

  const connectDropbox = () => {
    setConnecting(true);
    toast.info('Redirecting to Dropbox authorization...');
    // Redirect to Dropbox OAuth flow (backend handles redirect)
    window.location.href = `${API_URL}/api/storage/dropbox/connect`;
  };

  const disconnectDropbox = async () => {
    if (!confirm('Are you sure you want to disconnect Dropbox? This will not delete any files.')) {
      return;
    }

    try {
      setDisconnecting(true);
      await axios.post(
        `${API_URL}/api/storage/dropbox/disconnect`,
        {},
        { withCredentials: true }
      );
      toast.success('Dropbox disconnected');
      loadStatus();
    } catch (err) {
      console.error('Error disconnecting Dropbox:', err);
      toast.error('Failed to disconnect Dropbox');
    } finally {
      setDisconnecting(false);
    }
  };

  const setStorageMode = async (mode) => {
    try {
      setSavingMode(true);
      await axios.post(
        `${API_URL}/api/storage/settings`,
        { storage_mode: mode },
        { withCredentials: true }
      );
      toast.success(`Storage mode set to ${mode === 'dropbox' ? 'Dropbox' : 'Temporary Storage'}`);
      loadStatus();
    } catch (err) {
      console.error('Error setting storage mode:', err);
      toast.error(err.response?.data?.detail || 'Failed to update storage mode');
    } finally {
      setSavingMode(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const dropboxConnected = status?.dropbox?.is_connected && status?.dropbox?.is_active;
  const currentMode = status?.storage_mode || 'temp';

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Storage Settings</h1>
        <p className="text-gray-400">Manage your content storage preferences</p>
      </div>

      {/* Storage Mode Selector */}
      <div className="mb-8 bg-[#1a1d23] border border-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-4">Storage Mode</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Temp Storage Option */}
          <button
            onClick={() => setStorageMode('temp')}
            disabled={savingMode || currentMode === 'temp'}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              currentMode === 'temp'
                ? 'border-cyan-500 bg-cyan-500/10'
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            } ${savingMode ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Database className={`w-6 h-6 ${currentMode === 'temp' ? 'text-cyan-400' : 'text-gray-400'}`} />
              <span className={`font-semibold ${currentMode === 'temp' ? 'text-cyan-400' : 'text-white'}`}>
                Temporary Storage
              </span>
              {currentMode === 'temp' && (
                <CheckCircle className="w-5 h-5 text-cyan-400 ml-auto" />
              )}
            </div>
            <p className="text-sm text-gray-400">
              Files stored on our servers for {status?.temp_storage?.retention_hours || 72} hours. No setup required.
            </p>
          </button>

          {/* Dropbox Option */}
          <button
            onClick={() => dropboxConnected ? setStorageMode('dropbox') : connectDropbox()}
            disabled={savingMode || (currentMode === 'dropbox' && dropboxConnected)}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              currentMode === 'dropbox' && dropboxConnected
                ? 'border-green-500 bg-green-500/10'
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            } ${savingMode ? 'opacity-50' : ''}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <HardDrive className={`w-6 h-6 ${currentMode === 'dropbox' && dropboxConnected ? 'text-green-400' : 'text-gray-400'}`} />
              <span className={`font-semibold ${currentMode === 'dropbox' && dropboxConnected ? 'text-green-400' : 'text-white'}`}>
                Dropbox
              </span>
              {currentMode === 'dropbox' && dropboxConnected && (
                <CheckCircle className="w-5 h-5 text-green-400 ml-auto" />
              )}
            </div>
            <p className="text-sm text-gray-400">
              {dropboxConnected 
                ? 'Connected - Your files sync to your Dropbox account'
                : 'Click to connect your Dropbox account'}
            </p>
          </button>
        </div>
      </div>

      {/* Dropbox Connection Card */}
      <div className={`mb-8 p-6 rounded-lg border-2 ${
        dropboxConnected
          ? 'bg-green-500/10 border-green-500/30'
          : 'bg-gray-500/10 border-gray-500/30'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
              dropboxConnected ? 'bg-green-500/20' : 'bg-gray-500/20'
            }`}>
              <HardDrive className={`w-7 h-7 ${dropboxConnected ? 'text-green-400' : 'text-gray-400'}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Dropbox Connection</h2>
              <p className={`text-sm ${dropboxConnected ? 'text-green-300' : 'text-gray-400'}`}>
                {dropboxConnected ? 'Connected' : 'Not Connected'}
              </p>
            </div>
          </div>
          
          {dropboxConnected ? (
            <button
              onClick={disconnectDropbox}
              disabled={disconnecting}
              className="px-5 py-2 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </button>
          ) : (
            <button
              onClick={connectDropbox}
              disabled={connecting}
              className="px-5 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {connecting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect Dropbox'
              )}
            </button>
          )}
        </div>

        {/* Connection Details */}
        {dropboxConnected && (
          <div className="pt-4 border-t border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Account</p>
                <p className="text-white font-medium">{status.dropbox.account_email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Root Folder</p>
                <p className="text-white font-medium font-mono">{status.dropbox.root_folder_path}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">Connected</p>
                <p className="text-white font-medium">
                  {status.dropbox.created_at ? new Date(status.dropbox.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Folder Structure Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">How It Works</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>• Content is organized automatically by type</li>
                <li>• Live recordings, clips, and assets are separated</li>
                <li>• Access files anytime from your Dropbox</li>
                <li>• Automatic backup and sync</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Folder className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Folder Structure</h3>
              <div className="text-sm text-gray-300 font-mono space-y-1">
                <div>/consistency</div>
                <div className="ml-4">/Live/Raw, /Live/Replay</div>
                <div className="ml-4">/LongForm, /ShortForm</div>
                <div className="ml-4">/Vlogs, /Thumbnails</div>
                <div className="ml-4">/Assets, /Archive</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning for Temp Storage */}
      {currentMode === 'temp' && (
        <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold text-yellow-300 mb-1">Using Temporary Storage</h3>
              <p className="text-sm text-yellow-400/90">
                Files are stored for {status?.temp_storage?.retention_hours || 72} hours. Connect Dropbox for permanent storage.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dropbox Signup Link */}
      <div className="mt-6 text-center">
        <a
          href="https://www.dropbox.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors"
        >
          Need a Dropbox account? Sign up here
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
